/* eslint-disable camelcase */
import * as React from "react"
import * as yup from "yup"
import { Appearance, TouchableOpacity, ViewStyle, Dimensions, ScrollView } from "react-native"
import { BroodStockProduct, Cycle, Farm, Hatchery, Pond, Species } from "../../../database"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { FormProps } from "../form"
import { FieldArray, FieldArrayRenderProps, Formik, FormikProps, FormikValues } from "formik"
import { ModelField } from "../../field/model-field/model-field"
import { Divider, Icon as NativeBaseIcon, View } from "native-base"
import { color, nativeBaseTheme } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { FC, useCallback, useMemo, useRef, useState } from "react"
import moment from "moment"
import { Button } from "../../button/button"
import { useStores } from "../../../models"
import { NumeralField } from "../../field/numeral-field/numeral-field"
import { TextField } from "../../field/text-field/text-field"
import { PickerField } from "../../field/picker-field/picker-field"
import { Text } from "../../text/text"
import { JalaIcon } from "../../jala-icon/jala-icon"
import { navigate } from "../../../navigators"
import { useFocusEffect } from "@react-navigation/native"
import Icon from 'react-native-vector-icons/MaterialIcons';
import { DeleteModal } from "../../model-create-screen/model-create-screen"
import JalaAnalytic from "../../../utils/jala-analytic"
import { WalkthroughElement } from "../../walkthrough-element/walkthrough-element"
import _ from "lodash"

const windowWidth = Dimensions.get("window").width


const BORDER: ViewStyle = {
  backgroundColor: color.palette.background,
  height: 4,
}

const OTHERS: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingHorizontal: 16,
  // marginBottom: 14,
  width: windowWidth,
  // marginTop: 14,
  paddingBottom: 14,
  paddingTop: 14,
  backgroundColor: color.palette.white
}

const CUSTOM_FR_BTN: ViewStyle = {
  flexDirection: 'row',
  paddingHorizontal: 16,
  marginTop: 10
}

const DELETE_BUTTON: ViewStyle = {
  marginVertical: 8
}

const totalSeedTypes = [
  {
    id: 'net',
    get name() { return translate('cycleForm.net') },
    get note() { return translate('cycleForm.netNote') }
  },
  {
    id: 'gross',
    get name() { return translate('cycleForm.gross') },
    get note() { return translate('cycleForm.grossNote') }
  },
  {
    id: 'actual',
    get name() { return translate('cycleForm.actual') },
    get note() { return translate('cycleForm.actualNote') }
  },
]

const survivalRatePredictionMethods = [
  {
    id: null,
    get name() { return translate('cycleForm.fr') }
  },
  {
    id: 'fixed',
    get name() { return translate('cycleForm.fixed') }
  },
  {
    id: 'ai',
    get name() { return translate('cycleForm.ai') }
  },
  {
    id: 'custom_fr',
    get name() { return translate('cycleForm.customFr') }
  },
  {
    id: 'stp_fr',
    get name() { return translate('cycleForm.stpFr') }
  },
  {
    id: 'adg',
    get name() { return translate('cycleForm.adg') }
  },
]

const feedingTrayCount = [{ id: 1 }, { id: 2 }, { id: 3 }, { id: 4 } ]

const modelDefaultValues = {
  feeding_tray_count: 4,
  limit_weight_per_area: 1.25,
  target_cultivation_day: 120,
  target_size: 55,
  target_survival_rate: 75,
  survival_rate_prediction_method: null,
  area: null,
  deep: null,
  get started_at() { return moment().format('YYYY-MM-DD') }
}

export interface CycleCreateMultipleFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    farm_id?: number
    models?: Array<{
      id?: number
      pond_id?: number
      total_seed?: number
      started_at?: string
    }>
  }
}

const CycleCreateForm: FC<{ colorScheme?: "light"|"dark", values: any, index: number, parentScrollViewRef?: React.MutableRefObject<ScrollView> } & FieldArrayRenderProps> = observer(function CycleCreateForm(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    name,
    index,
    form: { values, initialValues, setFieldValue, setFieldTouched },
    parentScrollViewRef: scrollViewRef,
  } = props;

  const {
    appStore: { farmStore },
    cycleStore, cycleStore: { loading }
  } = useStores()

  const editing = !!(values.models[index] || {}).id;

  const [ hide, setHide ] = useState<boolean>(true)

  const iconName = hide ? "expand_more" : "expand_less"

  const handleDelete = useCallback(async () => {
    if (!cycleStore) return;
    cycleStore.set('loading', true)
    const model = Cycle.hydrate({ id: (values.models[index] || {}).id });
    const success = await cycleStore.delete(model)

    JalaAnalytic.log("Mobile app: Form Handle Delete", {
      form: CycleCreateMultipleForm.displayName || 'Unknown',
      success
    })

    if (success) {
      farmStore.initWithLastBatchAndWaterQualityMultiMeasurements(() => {
        cycleStore.set('needRefresh', false);
        cycleStore.set('pushIdQueue', {})
      });
      navigate('Home.Stack', 'Home.Farm.Pond.List')
    } else {
      cycleStore.set('needRefresh', false);
      cycleStore.set('pushIdQueue', {})
    }
    cycleStore.set('loading', false)
  }, [cycleStore, farmStore, index, values.models])

  return <>
    <ModelField
      key={`${values.farm_id}-${name}-${index}-pond_id`}
      name={`${name}.${index}.pond_id`}
      txPrefix="cycleForm"
      colorScheme={colorScheme}
      formControlProps={{ isRequired: true }}
      modelPickerProps={{
        Model: Pond,
        fetchData: !(initialValues[name][index] || {}).pond_id ? (model, search) => (
          Promise.resolve(farmStore.inactivePonds.map((p) => Pond.hydrate(_.omit(p, ['area']))))
            .then((ponds: Array<Pond>) => ponds.filter((p: Pond) => (
              p.getAttribute("name").toLowerCase().includes((search || "").toLowerCase())
            )))
        ) : undefined,
        onValueChange: async (value, i, item) => {
          await setFieldValue(`${name}.${index}.area`, item && item.area || null)
          await setFieldValue(`${name}.${index}.deep`, item && item.deep || null)
          await setFieldValue(`${name}.${index}.pond_id`, value || null)
          await setFieldTouched(`${name}.${index}.pond_id`)
        },
        clearable: true,
        isDisabled: !values.farm_id || !!(initialValues[name][index] || {}).pond_id,
        titleTx: "cycleForm.pondIdPlaceholder",
      }}
    />
    <View flexDirection="row">
      <View flex={1}>
        <NumeralField
          name={`${name}.${index}.total_seed`}
          txPrefix="cycleForm"
          colorScheme={colorScheme}
          formControlProps={{ isRequired: true }}
          // numeralInputProps={{ placeholderTx: "cycleForm.totalSeed" }}
        />
      </View>
      <View flex={1}>
        <PickerField
          name={`${name}.${index}.total_seed_type`}
          txPrefix="cycleForm"
          colorScheme={colorScheme}
          formControlProps={{ isRequired: true }}
          pickerProps={{
            data: totalSeedTypes,
            renderItemText: (item) => item && `${item.name} (${item.note})`,
            titleTx: "cycleForm.totalSeedTypePlaceholder",
            // placeholderTx: "cycleForm.totalSeedType"
          }}
        />
      </View>
    </View>
    <DateTimePickerField
      name={`${name}.${index}.started_at`}
      txPrefix="cycleForm"
      colorScheme={colorScheme}
      formControlProps={{ isRequired: true }}
      // formControlProps={{ isRequired: true  }}
    />
    {editing && (
      <View background={color.palette.orange300} py="12px" px="16px" my="10px" flexDirection="row">
        <Icon name="info-outline" size={20} color={color.palette.orange} />
        <Text tx="cycleForm.warning" ml="10px" preset="body2" color={color.palette.grey} colorScheme={colorScheme}
              numberOfLines={3} />
      </View>
    )}
    <View flexDirection="row">
      <View flex={1}>
        <NumeralField
          name={`${name}.${index}.initial_age`}
          txPrefix="cycleForm"
          colorScheme={colorScheme}
          formControlProps={{ helperTextTx: "cycleForm.initialAgeHelper" }}
        />
      </View>
      <View flex={1}>
        <NumeralField
          name={`${name}.${index}.preparation_day`}
          txPrefix="cycleForm"
          colorScheme={colorScheme}
          formControlProps={{ helperText: "." }}
        />
      </View>
    </View>
    <ModelField
      name={`${name}.${index}.hatchery_id`}
      txPrefix="cycleForm"
      colorScheme={colorScheme}
      modelPickerProps={{
        Model: Hatchery,
        clearable: true,
        titleTx: "cycleForm.hatcheryIdPlaceholder",
      }}
    />
    <ModelField
      name={`${name}.${index}.species_id`}
      txPrefix="cycleForm"
      colorScheme={colorScheme}
      modelPickerProps={{
        Model: Species,
        sortBy: "id",
        titleTx: "cycleForm.speciesIdPlaceholder",
        pickerActionsheetProps: {
          withoutSearch: true,
        },
      }}
    />
    <WalkthroughElement
      id="cycle-form-second"
      scrollViewRef={scrollViewRef}
      scrollOffset={250}
    >
      <View style={OTHERS}>
        <Text tx="cycleForm.targetAndCycles" preset="title3" colorScheme={colorScheme} color={color.palette.gray800} />
        <TouchableOpacity onPress={() => setHide(!hide)}>
          {/* <Icon {...nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme })} */}
          <NativeBaseIcon {...nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme })}
                          as={<JalaIcon name={iconName} />} size="24px" />
        </TouchableOpacity>
      </View>
    </WalkthroughElement>
    {!hide && <>
      <Text tx="cycleForm.targetSubtitle" preset="xs" px="16px" colorScheme={colorScheme}
            color={color.palette.darkGrey} />
      {!editing && <ModelField
        name={`${name}.${index}.brood_stock_product_id`}
        txPrefix="cycleForm"
        colorScheme={colorScheme}
        modelPickerProps={{
          Model: BroodStockProduct,
          dataLabel: "supplier_product_name",
          fetchData: (model, search, params) => (
            model.fetch({
              search,
              ...params,
              sort: 'brood_stock_supplier.name|name',
            })
          ),
          clearable: true,
          titleTx: "cycleForm.broodStockProductIdPlaceholder",
        }}
      />}
      <PickerField
        name={`${name}.${index}.feeding_tray_count`}
        txPrefix="cycleForm"
        colorScheme={colorScheme}
        pickerProps={{
          data: feedingTrayCount,
          dataLabel: "id",
          clearable: false,
          renderItemText: (item) => item && `${item.id}`,
          renderSelectedItemText: (item) => item && `${item.id}`,
          titleTx: "cycleForm.feedingTrayCountPlaceholder",
        }}
      />
      <NumeralField
        name={`${name}.${index}.limit_weight_per_area`}
        txPrefix="cycleForm"
        colorScheme={colorScheme}
      />
      <NumeralField
        name={`${name}.${index}.target_cultivation_day`}
        txPrefix="cycleForm"
        colorScheme={colorScheme}
      />
      <View flexDirection="row">
        <View flex={1}>
          <NumeralField
            name={`${name}.${index}.target_size`}
            txPrefix="cycleForm"
            colorScheme={colorScheme}
            formControlProps={{ helperTextTx: "cycleForm.targetSizeHelper" }}
          />
        </View>
        <View flex={1}>
          <NumeralField
            name={`${name}.${index}.target_survival_rate`}
            txPrefix="cycleForm"
            colorScheme={colorScheme}
          />
        </View>
      </View>
      <NumeralField
        name={`${name}.${index}.area`}
        txPrefix="cycleForm"
        colorScheme={colorScheme}
      />
      <NumeralField
        name={`${name}.${index}.deep`}
        txPrefix="cycleForm"
        colorScheme={colorScheme}
      />
      <PickerField
        name={`${name}.${index}.survival_rate_prediction_method`}
        txPrefix="cycleForm"
        colorScheme={colorScheme}
        pickerProps={{
          data: survivalRatePredictionMethods,
          titleTx: "cycleForm.survivalRatePredictionMethodPlaceholder",
        }}
      />
      {values.models[index].survival_rate_prediction_method === "custom_fr" && (
        <View style={CUSTOM_FR_BTN}>
          <Text preset="caption2" color={color.palette.gray600} tx="customFRScreen.create" mr="2px"
                colorScheme={colorScheme} />
          <Button
            testID="jala:id/custom-fr-button"
            onPress={() => navigate("Home.Stack", "Home.Cycle.CustomFRTable")}
            preset="link"
            textPreset="caption2"
            colorScheme={colorScheme}
            tx="customFRScreen.here"
            textColor={color.palette.blueJalaDarker}
          />
        </View>
      )}
      <TextField
        name={`${name}.${index}.remark`}
        txPrefix="cycleForm"
        colorScheme={colorScheme}
        formControlProps={{ inputProps: { multiline: true, numberOfLines: 3, onSubmitEditing: undefined } }}
      />
    </>}
    {editing && <View marginTop="8px" padding="16px">
      <View>
        <Text tx="cycleCreateMultipleScreen.deleteCycle" preset="title3" colorScheme={colorScheme} marginBottom="4px" />
        <Text tx="cycleCreateMultipleScreen.deleteCycleSubtitle" preset="body3" colorScheme={colorScheme}
              color={nativeBaseTheme.colors.gray[colorScheme === "light" ? "700" : "300"]} />
      </View>
      <DeleteModal
        testID="jala:id/delete-button"
        style={DELETE_BUTTON}
        onConfirm={handleDelete}
        deleteModalTitle={translate("cycleCreateMultipleScreen.deleteCycleModalTitle")}
        deleteModalSubtitle={translate("cycleCreateMultipleScreen.deleteCycleModalSubtitle")}
        disabled={loading}
      >
        <Text
          tx="delete"
          preset="title4"
          colorScheme={colorScheme}
          color={loading ? nativeBaseTheme.colors.muted["500"] : nativeBaseTheme.colors.danger["500"]}
        />
      </DeleteModal>
    </View>}
  </>
})

const FormikField: React.FC<{ colorScheme?: "light"|"dark", loading?: boolean, denyAddOnMultiple?: boolean, parentScrollViewRef?: React.MutableRefObject<ScrollView> } & FormikProps<any>> = observer(function FormikField(props) {
  const {
    colorScheme, initialValues, setFieldValue, setFieldTouched, loading, denyAddOnMultiple,
    parentScrollViewRef
  } = props
  const { appStore } = useStores()

  const renderFieldArray = useCallback((props: FieldArrayRenderProps) => {
    const { name, push, remove, form: { values } } = props

    return <>
      {(values.models || []).map((model, index) => <View key={`${values.farm_id}-${name}-${index}-${values.models.length}`}>
        <CycleCreateForm
          colorScheme={colorScheme}
          values={model}
          index={index}
          parentScrollViewRef={parentScrollViewRef}
          {...props}
        />
        {values[name].length > 1 && !values[name][index]._denyDelete && <View alignItems="flex-end" paddingBottom="8px" paddingX="16px">
          <Button
            testID="jala:id/delete-button"
            tx="delete"
            textPreset="title4"
            preset="link"
            colorScheme={colorScheme}
            disabled={loading}
            textColor={loading ? nativeBaseTheme.colors.muted["500"] : nativeBaseTheme.colors.danger["500"]}
            onPress={() => remove(index)}
          />
        </View>}
        <Divider />
      </View>)}
      {!denyAddOnMultiple && <View alignItems="flex-end" paddingY="8px" paddingX="16px">
        <Button
          testID="jala:id/add-button"
          tx="cycleForm.nextPond"
          textPreset="title4"
          preset="link"
          colorScheme={colorScheme}
          disabled={loading}
          textColor={loading ? nativeBaseTheme.colors.muted["500"] : nativeBaseTheme.colors.primary["500"]}
          onPress={() => push({
            ...modelDefaultValues,
          })}
        />
      </View>}
    </>
  }, [denyAddOnMultiple, colorScheme, loading, parentScrollViewRef])

  const fetchFarmData = useCallback((model, search) => Promise.resolve(
    appStore.farms.filter((f) => f.name.toUpperCase().includes((search || '').toUpperCase()))
      .map((f) => Farm.hydrate(f))
  ), [appStore])

  return <>
    <ModelField
      name="farm_id"
      txPrefix="cycleForm"
      colorScheme={colorScheme}
      formControlProps={{ isRequired: true }}
      modelPickerProps={{
        Model: Farm,
        isDisabled: !!initialValues.farm_id,
        onValueChange: async (value) => {
          await setFieldValue("models", [])
          await setFieldValue("farm_id", value || null)
          await setFieldTouched("farm_id")
        },
        fetchData: fetchFarmData,
        InputRightElement: !!initialValues.farm_id && <></> || undefined,
        style: { borderWidth: 0, backgroundColor: color.palette.white }
      }}
    />
    <View style={BORDER} />
    <FieldArray
      name="models"
      render={renderFieldArray}/>
  </>
})

/**
 * Describe your component here
 */
export const CycleCreateMultipleForm = observer(function CycleCreateMultipleForm(props: CycleCreateMultipleFormProps) {
  const {
    initialValues: initialValuesProps = {}, onSubmit, colorScheme = Appearance.getColorScheme(), innerRef: innerRefProps,
    loading,
    denyAddOnMultiple,
    parentScrollViewRef,
    ...rest
  } = props

  const [ frCount, setFrCount ] = useState<boolean>(false);

  const { farmStore } = useStores()

  const modelForm = useRef<FormikProps<FormikValues>>()
  const innerRef = useCallback((node) => {
    modelForm.current = node;
    if (innerRefProps instanceof Function)
    innerRefProps(node);
  }, [innerRefProps])

  useFocusEffect(useCallback(() => {
    const unsubscribe = farmStore.fetchFrTable().then(result => {
      const table = ((result[0] || {}).data || [])
      const fr = table.filter(data => data.doc >= 1 && data.doc <= 60 && data.abw > 0 && data.fr > 0).length;
      const isFilled = fr >= 60
      setFrCount(isFilled);
      // survival_rate_prediction_method
    })
    .catch(err => console.log("ERROR GET FR ===> ", err))
    return () => unsubscribe;
  }, [farmStore]))

  React.useEffect(() => {
    modelForm.current.validateField('models')
  }, [frCount])

  const validationSchema = useMemo(() => yup.object({
    farm_id: yup.number().nullable().required().label(translate("cycleForm.farmId")),
    models: yup.array().of(yup.object({
      pond_id: yup.lazy((value, options: any) => {
        return yup.number()
          .notOneOf(
            modelForm.current && modelForm.current.values.models.map(m => m.pond_id).filter((m, i) => i !== options.index) || [],
            translate('yup.array.unique')
          )
          .nullable().required()
          .label(translate("cycleForm.pondId"))
      }),
      total_seed: yup.number().moreThan(0).nullable().required().label(translate("cycleForm.totalSeed")),
      total_seed_type: yup.string().oneOf(totalSeedTypes.map((t) => t.id)).nullable().required().label(translate("cycleForm.totalSeedType")),
      started_at: yup.mixed().nullable().required().label(translate("cycleForm.startedAt")),
      initial_age: yup.number().min(0).nullable().label(translate("cycleForm.initialAge")),
      preparation_day: yup.number().moreThan(0).lessThan(45).nullable().label(translate("cycleForm.preparationDay")),
      hatchery_id: yup.number().nullable().label(translate("cycleForm.hatcheryId")),
      species_id: yup.number().nullable().label(translate("cycleForm.speciesId")),
      brood_stock_product_id: yup.number().nullable().label(translate("cycleForm.broodStockProductId")),
      feeding_tray_count: yup.number().moreThan(0).max(4).nullable().label(translate("cycleForm.preparationDay")),
      limit_weight_per_area: yup.number().moreThan(0).nullable().label(translate("cycleForm.limitWeightPerArea")),
      target_cultivation_day: yup.number().moreThan(0).max(160).nullable().label(translate("cycleForm.targetCultivationDay")),
      target_size: yup.number().moreThan(10).max(200).nullable().label(translate("cycleForm.targetSize")),
      target_survival_rate: yup.number().moreThan(0).max(200).nullable().label(translate("cycleForm.targetSize")),
      area: yup.number().moreThan(0).max(1000000).nullable().label(translate("cycleForm.area")),
      deep: yup.number().moreThan(0).max(100).nullable().label(translate("cycleForm.deep")),
      // survival_rate_prediction_method: yup.string().oneOf(survivalRatePredictionMethods.map((m) => m.id)).nullable().label(translate("cycleForm.survivalRatePredictionMethod")),
      survival_rate_prediction_method: yup.string().oneOf(survivalRatePredictionMethods.map((m) => m.id)).test("fr", translate("cycleForm.minimumFr"),
        (value) => new Promise((resolve) => {
          if (value === 'custom_fr') {
            if (frCount) resolve(true);
            else resolve(false);
          } else resolve(true);
        })
      ).nullable().label(translate("cycleForm.survivalRatePredictionMethod")),
      remark: yup.string().ascii().max(255).nullable().label(translate("cycleForm.remark")),
    })).nullable().required(),
  }), [frCount])

  const initialValues = useMemo<CycleCreateMultipleFormProps["initialValues"]>(() => {
    const models = initialValuesProps.models || [];
    return ({
      farm_id: null,
      ...initialValuesProps,
      models: models.length ? models.map((m) => ({
        ...(!m.id && modelDefaultValues || {}),
        ...m,
      })) : [{
        ...modelDefaultValues
      }]
    })
  }, [initialValuesProps])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {(formikProps) => (
        <FormikField
          colorScheme={colorScheme}
          loading={loading}
          denyAddOnMultiple={denyAddOnMultiple}
          parentScrollViewRef={parentScrollViewRef}
          {...formikProps}
        />
      )}
    </Formik>
  )
})

CycleCreateMultipleForm.displayName = 'CycleCreateMultipleForm'
